import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { MailCheck, RefreshCw, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import SettingsToggle from "@/components/SettingsToggle";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

export default function EmailVerificationRequired() {
  const { user, refresh } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setTimeout(() => setCooldown((seconds) => seconds - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  useEffect(() => {
    const checkOnReturn = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    document.addEventListener("visibilitychange", checkOnReturn);
    window.addEventListener("focus", checkOnReturn);
    return () => {
      document.removeEventListener("visibilitychange", checkOnReturn);
      window.removeEventListener("focus", checkOnReturn);
    };
  }, [refresh]);

  const resend = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/auth/resend-verification", {});
      return response.json();
    },
    onSuccess: () => {
      setCooldown(60);
      toast({ title: "Verification email sent", description: "Check your inbox, then return here." });
    },
    onError: (error: Error) => {
      toast({ title: "Couldn't send email", description: error.message, variant: "destructive" });
    },
  });

  const signOut = useMutation({
    mutationFn: () => apiRequest("POST", "/api/auth/logout"),
    onSuccess: async () => {
      queryClient.setQueryData(["/api/auth/user"], null);
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
    },
  });

  return (
    <div className="min-h-screen cosmic-bg flex items-center justify-center py-6">
      <div className="w-full max-w-md">
        <div className="flex justify-end mb-3"><SettingsToggle /></div>
        <Card className="cosmic-card border-0 shadow-lg overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500" />
          <CardHeader className="text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-3">
              <MailCheck className="w-8 h-8 text-primary" />
            </div>
            <CardTitle>Verify your email to continue</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-center">
            <p className="text-sm text-muted-foreground">
              We sent a verification link to <span className="font-medium text-foreground">{user?.email}</span>.
              Open it in your email, then return to ZEASTRO.
            </p>
            <Button className="w-full" variant="outline" onClick={() => void refresh()}>
              <RefreshCw className="w-4 h-4 mr-2" /> I’ve verified my email — check again
            </Button>
            <Button
              className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white"
              disabled={resend.isPending || cooldown > 0}
              onClick={() => resend.mutate()}
            >
              {resend.isPending ? "Sending…" : cooldown > 0 ? `Resend in ${cooldown}s` : "Resend verification email"}
            </Button>
            <Button className="w-full" variant="ghost" disabled={signOut.isPending} onClick={() => signOut.mutate()}>
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}